'use client';

import { useState, useEffect, useCallback, createContext, useContext, useRef } from 'react';
import { useRouter } from 'next/navigation';
import { createClient } from '@/lib/supabase/client';
import { User } from '@supabase/supabase-js';
import AuthModal from '@/app/components/AuthModal';
import SubscriptionModal from '@/app/components/SubscriptionModal';
import AnonScrollSignupPrompt from './AnonScrollSignupPrompt';
import { 
  getEntitlements, 
  readEntitlementsCache, 
  getEntitlementsStatus,
  setupEntitlementsBroadcastListener,
  readPremiumEverFlag,
  type EntitlementsStatus
} from '@/lib/entitlements';
import { useNavigationReady } from '@/app/contexts/NavigationReadyContext';
import { useAuth } from '@/app/providers/AuthProvider';
import BrailleSpinner from '@/app/components/BrailleSpinner';
import { trackStockViewed } from '@/lib/analytics/stock';
import { ShareNavProvider } from './ShareNavContext';

// BroadcastChannel для синхронизации между вкладками
const AUTH_CHANNEL_NAME = 'bastion_auth_sync';
let authChannel: BroadcastChannel | null = null;
if (typeof window !== 'undefined') {
  try {
    authChannel = new BroadcastChannel(AUTH_CHANNEL_NAME);
  } catch (e) {
    console.warn('BroadcastChannel not supported, falling back to localStorage');
  }
}

// Кэш состояния авторизации в localStorage
const AUTH_CACHE_KEY = 'bastion_auth_cache';
const AUTH_CACHE_TIMESTAMP_KEY = 'bastion_auth_cache_timestamp';
const AUTH_CACHE_TTL = 10 * 60 * 1000; // 10 минут

// Очередь запросов авторизации для предотвращения одновременных запросов
let authRequestQueue: Array<() => Promise<any>> = [];
let isProcessingAuthQueue = false;

// Глобальный флаг для предотвращения одновременных проверок на всех вкладках
const GLOBAL_CHECK_KEY = 'bastion_global_auth_check';
const GLOBAL_CHECK_TTL = 5000; // 5 секунд - если проверка была недавно, не делаем еще одну

// Вспомогательная функция для чтения кэша при инициализации (до определения getCachedAuth)
function readInitialCache(): { isPaid: boolean; status: EntitlementsStatus; wasPremiumEver: boolean } | null {
  if (typeof window === 'undefined') return null;
  try {
    const { readEntitlementsCache, readPremiumEverFlag } = require('@/lib/entitlements');
    const cached = localStorage.getItem(AUTH_CACHE_KEY);
    const timestamp = localStorage.getItem(AUTH_CACHE_TIMESTAMP_KEY);
    
    // Читаем долгоживущий флаг "был премиум"
    const premiumEverFlag = readPremiumEverFlag();
    const wasPremiumEver = premiumEverFlag?.wasPremium ?? false;
    
    if (cached && timestamp) {
      const age = Date.now() - parseInt(timestamp);
      if (age < AUTH_CACHE_TTL) {
        const user = cached === 'null' ? null : JSON.parse(cached);
        if (user) {
          const entitlementsCache = readEntitlementsCache();
          if (entitlementsCache && entitlementsCache.userId === user.id) {
            return {
              isPaid: entitlementsCache.isPaid,
              status: entitlementsCache.isPaid ? 'paid' : 'free',
              wasPremiumEver: wasPremiumEver || entitlementsCache.isPaid
            };
          }
          
          // Если кэш entitlements истек, но есть флаг premium_ever - используем его
          if (wasPremiumEver && premiumEverFlag?.userId === user.id) {
            return {
              isPaid: true, // Оптимистично показываем premium пока не проверим
              status: 'paid',
              wasPremiumEver: true
            };
          }
        }
      } else {
        // Кэш авторизации устарел, но проверяем премиум флаг
        const user = cached === 'null' ? null : JSON.parse(cached);
        if (user && wasPremiumEver && premiumEverFlag?.userId === user.id) {
          return {
            isPaid: true, // Оптимистично показываем premium
            status: 'paid',
            wasPremiumEver: true
          };
        }
      }
    }
  } catch (e) {
    // Игнорируем ошибки при инициализации
  }
  return null;
}

interface Top30Stock {
  FullTicker: string;
  ShortName: string | null;
  Name: string | null;
  Country: string | null;
  Industry: string | null;
}

// Computed once on the server and refined on the client. Drives both the
// paywall overlay and whether gated content is rendered into the DOM at all
// (see BlurredContent).
//   - 'anonymous'    — not logged in. Sees the same free tier as a
//                      registered free user (Key Ratings + Momentum + Growth); the
//                      register modal opens on scroll (AnonScrollSignupPrompt).
//   - 'within-limit' — logged-in free user (depth-gate: Key Ratings + Momentum + Growth
//                      free; Value onward paid). Name kept for analytics
//                      continuity; there is no monthly view cap.
//   - 'over-limit'   — legacy access level (unused). Kept in the union so
//                      older analytics payloads still type-check.
//   - 'paid'         — active or trialing subscription; full access.
export type StockAccessLevel = 'anonymous' | 'within-limit' | 'over-limit' | 'paid';

interface AuthContextType {
  user: User | null;
  isPaid: boolean;
  shouldBlur: boolean;
  accessLevel: StockAccessLevel;
  entitlementsStatus: EntitlementsStatus; // 'unknown' | 'free' | 'paid'
  wasPremiumEver: boolean;
  authModalDismissed: boolean;
  subscriptionDismissed: boolean;
  setSubscriptionDismissed: (dismissed: boolean) => void;
  top30Stocks: Top30Stock[];
  setTop30Stocks: (stocks: Top30Stock[]) => void;
  setAuthModalOpen: (open: boolean) => void;
  setAuthMode: (mode: 'login' | 'register') => void;
  setSubscriptionModalOpen: (open: boolean) => void;
  /** Open the upgrade modal with optional surface-specific copy. */
  openUpgradeModal: (opts?: { title?: string; message?: string; highlightFeature?: string }) => void;
}

const AuthContext = createContext<AuthContextType | null>(null);
export { AuthContext as StockPageAuthContext };

export const useStockPageAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useStockPageAuth must be used within StockPageAuthWrapper');
  }
  return context;
};

interface StockPageAuthWrapperProps {
  children: React.ReactNode;
  fullTicker: string;
  stockMeta?: {
    ticker?: string;
    name?: string;
    sector?: string;
    country?: string;
  };
  // Access level computed on the server during SSR. Used as the initial value
  // for the client state machine to avoid a hydration flicker between
  // blurred / unblurred. The client may upgrade this (e.g. on login) but
  // should never silently downgrade paid → free without an API confirmation
  // (see safeSetIsPaid below).
  initialAccessLevel?: StockAccessLevel;
}

export default function StockPageAuthWrapper({
  children,
  fullTicker,
  stockMeta,
  initialAccessLevel,
}: StockPageAuthWrapperProps) {
  const router = useRouter();
  const { isNavigationReady } = useNavigationReady();
  const { user: authUser, authLoading } = useAuth(); // Используем единый AuthProvider
  const [user, setUser] = useState<User | null>(null); // Локальный state для совместимости
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [authModalDismissed, setAuthModalDismissed] = useState(false);
  const [subscriptionModalOpen, setSubscriptionModalOpen] = useState(false);
  const [subscriptionModalMessage, setSubscriptionModalMessage] = useState<string | null>(null);
  const [subscriptionModalTitle, setSubscriptionModalTitle] = useState<string | null>(null);
  const [subscriptionHighlightFeature, setSubscriptionHighlightFeature] = useState<string | null>(null);
  const [subscriptionDismissed, setSubscriptionDismissed] = useState(false);
  const [top30Stocks, setTop30Stocks] = useState<Top30Stock[]>([]);
  const [authMode, setAuthMode] = useState<'login' | 'register'>('register');
  
  // Инициализируем isPaid из кэша при первом рендере.
  // initialAccessLevel из сервера имеет приоритет — это авторитетный источник,
  // который не требует обращения к localStorage.
  const [isPaid, setIsPaid] = useState(() => {
    if (initialAccessLevel === 'paid') return true;
    if (initialAccessLevel) return false;
    const cached = readInitialCache();
    if (cached) {
      return cached.isPaid;
    }
    return false;
  });

  // Инициализируем entitlementsStatus из кэша при первом рендере
  const [entitlementsStatus, setEntitlementsStatus] = useState<EntitlementsStatus>(() => {
    if (initialAccessLevel === 'paid') return 'paid';
    if (initialAccessLevel === 'within-limit' || initialAccessLevel === 'over-limit') return 'free';
    if (initialAccessLevel === 'anonymous') return 'free';
    const cached = readInitialCache();
    if (cached) {
      return cached.status;
    }
    return 'unknown';
  });
  const [isChecking, setIsChecking] = useState(false); // Начинаем с false, устанавливаем в true только при необходимости

  // Free users can access stock pages — depth-gate (Key Ratings + Momentum + Growth free).

  const hasLoadedOnceRef = useRef(false);
  const isInitialMountRef = useRef(true);
  const isLoadingRef = useRef(false); // Защита от множественных одновременных вызовов
  const entitlementsRequestIdRef = useRef(0); // Для защиты от гонок при обновлении entitlements
  // Инициализируем wasPaidRef СИНХРОННО из кэша при первом рендере
  // Используем wasPremiumEver флаг для более надежного "sticky premium"
  const wasPaidRef = useRef((() => {
    const cached = readInitialCache();
    // Если был премиум когда-либо (в течение 7 дней) - используем это значение
    return cached?.wasPremiumEver ?? cached?.isPaid ?? false;
  })()); // Вызываем IIFE сразу
  const entitlementsUpdateTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastEntitlementsStatusRef = useRef<{ isPaid: boolean; status: EntitlementsStatus } | null>(null);

  // Helper функция для безопасной установки isPaid с "липким premium" и дебаунсингом
  const safeSetIsPaid = useCallback((newIsPaid: boolean, fromConfirmedSource = false) => {
    // Если пользователь был premium, не понижаем до free без подтвержденного ответа
    if (wasPaidRef.current && !newIsPaid && !fromConfirmedSource) {
      console.log('[StockPageAuthWrapper] Sticky premium: keeping paid status, ignoring downgrade to free');
      return; // Не обновляем isPaid, оставляем premium
    }
    
    // Проверяем, изменился ли статус
    const lastStatus = lastEntitlementsStatusRef.current;
    if (lastStatus && lastStatus.isPaid === newIsPaid) {
      return; // Статус не изменился, не обновляем
    }
    
    // Обновляем ref при установке premium
    if (newIsPaid) {
      wasPaidRef.current = true;
    }
    
    // Дебаунсим обновление UI
    if (entitlementsUpdateTimeoutRef.current) {
      clearTimeout(entitlementsUpdateTimeoutRef.current);
    }
    
    entitlementsUpdateTimeoutRef.current = setTimeout(() => {
      setIsPaid(newIsPaid);
      lastEntitlementsStatusRef.current = { 
        isPaid: newIsPaid, 
        status: newIsPaid ? 'paid' : 'free' 
      };
    }, 100); // 100ms дебаунс
  }, []);

  // Helper функция для безопасной установки entitlementsStatus с дебаунсингом
  const safeSetEntitlementsStatus = useCallback((status: EntitlementsStatus) => {
    const lastStatus = lastEntitlementsStatusRef.current;
    if (lastStatus && lastStatus.status === status) {
      return; // Статус не изменился
    }
    
    // Sticky premium: не понижаем до free если был paid
    if (wasPaidRef.current && status === 'free' && lastStatus && lastStatus.status === 'paid') {
      console.log('[StockPageAuthWrapper] Sticky premium: keeping paid status in entitlementsStatus');
      return; // Не обновляем статус, оставляем paid
    }
    
    if (entitlementsUpdateTimeoutRef.current) {
      clearTimeout(entitlementsUpdateTimeoutRef.current);
    }
    
    entitlementsUpdateTimeoutRef.current = setTimeout(() => {
      setEntitlementsStatus(status);
      // Обновляем ref для отслеживания последнего статуса
      const newIsPaid = status === 'paid';
      if (lastEntitlementsStatusRef.current) {
        lastEntitlementsStatusRef.current.status = status;
        lastEntitlementsStatusRef.current.isPaid = newIsPaid;
      } else {
        lastEntitlementsStatusRef.current = { 
          isPaid: newIsPaid, 
          status 
        };
      }
    }, 100);
  }, []);

  // Функция для получения кэшированного состояния авторизации
  const getCachedAuth = (): { user: User | null; timestamp: number } | null => {
    if (typeof window === 'undefined') return null;
    
    try {
      const cached = localStorage.getItem(AUTH_CACHE_KEY);
      const timestamp = localStorage.getItem(AUTH_CACHE_TIMESTAMP_KEY);
      
      if (cached && timestamp) {
        const age = Date.now() - parseInt(timestamp);
        if (age < AUTH_CACHE_TTL) {
          return {
            user: cached === 'null' ? null : JSON.parse(cached),
            timestamp: parseInt(timestamp)
          };
        }
      }
    } catch (e) {
      console.warn('Error reading auth cache:', e);
    }
    
    return null;
  };

  // Функция для сохранения состояния авторизации в кэш
  const setCachedAuth = (user: User | null, forceClear = false) => {
    if (typeof window === 'undefined') return;
    
    try {
      // Если forceClear=true, это явный sign out - очищаем кэш
      if (forceClear || !user) {
        localStorage.removeItem(AUTH_CACHE_KEY);
        localStorage.removeItem(AUTH_CACHE_TIMESTAMP_KEY);
        // НЕ отправляем AUTH_UPDATE с null - это может вызвать ложные logout на других вкладках
        // Supabase сам синхронизирует SIGNED_OUT через storage events
        return;
      }
      
      localStorage.setItem(AUTH_CACHE_KEY, JSON.stringify(user));
      localStorage.setItem(AUTH_CACHE_TIMESTAMP_KEY, Date.now().toString());
      
      // Отправляем сообщение другим вкладкам через BroadcastChannel только при обновлении пользователя
      if (authChannel) {
        authChannel.postMessage({
          type: 'AUTH_UPDATE',
          user: { id: user.id, email: user.email },
          timestamp: Date.now()
        });
      }
    } catch (e) {
      console.warn('Error setting auth cache:', e);
    }
  };

  // Обработчик сообщений от других вкладок
  useEffect(() => {
    if (!authChannel) return;
    
    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type === 'AUTH_UPDATE') {
        // Если другая вкладка обновила авторизацию, обновляем кэш
        const { user, timestamp } = event.data;
        if (typeof window !== 'undefined') {
          try {
            // НЕ записываем 'null' - только удаляем или обновляем
            if (user) {
              localStorage.setItem(AUTH_CACHE_KEY, JSON.stringify(user));
              localStorage.setItem(AUTH_CACHE_TIMESTAMP_KEY, timestamp.toString());
            }
            // Если user null, НЕ трогаем кэш - это может быть временный null
            // Кэш очищается только при явном SIGNED_OUT событии
          } catch (e) {
            console.warn('Error updating auth cache from broadcast:', e);
          }
        }
      }
    };
    
    authChannel.addEventListener('message', handleMessage);
    return () => {
      authChannel?.removeEventListener('message', handleMessage);
    };
  }, []);

  // Функция для выполнения запроса авторизации с задержкой (очередь)
  const executeAuthRequest = async (): Promise<{ user: User | null; error: any }> => {
    return new Promise((resolve) => {
      const requestFn = async () => {
        try {
          const supabase = createClient();
          
          // Сначала пробуем получить сессию (быстрее, чем getUser)
          const { data: { session }, error: sessionError } = await supabase.auth.getSession();
          
          if (session?.user && !sessionError) {
            return { user: session.user, error: null };
          }
          
          // Если сессии нет, пробуем getUser с таймаутом
          const getUserPromise = supabase.auth.getUser();
          const timeoutPromise = new Promise<{ data: { user: any }, error: any }>((resolve) => {
            setTimeout(() => {
              resolve({ data: { user: null }, error: { message: 'Timeout' } });
            }, 2000); // Уменьшили таймаут до 2 секунд
          });
          
          const result = await Promise.race([getUserPromise, timeoutPromise]);
          return { user: result.data?.user || null, error: result.error || null };
        } catch (error) {
          return { user: null, error: error as any };
        }
      };
      
      authRequestQueue.push(async () => {
        const result = await requestFn();
        resolve(result);
      });
      
      // Обрабатываем очередь последовательно
      if (!isProcessingAuthQueue) {
        isProcessingAuthQueue = true;
        const processQueue = async () => {
          try {
            while (authRequestQueue.length > 0) {
              const nextRequest = authRequestQueue.shift();
              if (!nextRequest) continue;

              try {
                await nextRequest();
              } catch (e) {
                console.error("Auth queue item failed:", e);
                // Важно: не вылетаем из processQueue, продолжаем обработку очереди
              }

              // Небольшая задержка между запросами для предотвращения конфликтов
              await new Promise(resolve => setTimeout(resolve, 100));
            }
          } finally {
            // Гарантированно сбрасываем флаг обработки очереди, даже при ошибке
            isProcessingAuthQueue = false;
          }
        };
        processQueue();
      }
    });
  };

  // Проверяем, не идет ли уже проверка на другой вкладке
  const isGlobalCheckInProgress = useCallback(() => {
    if (typeof window === 'undefined') return false;
    try {
      const lastCheck = localStorage.getItem(GLOBAL_CHECK_KEY);
      if (lastCheck) {
        const age = Date.now() - parseInt(lastCheck, 10);
        if (age < GLOBAL_CHECK_TTL) {
          return true; // Недавно была проверка, пропускаем
        }
      }
      return false;
    } catch (e) {
      return false;
    }
  }, []);

  // Помечаем, что началась проверка
  const markGlobalCheckStart = useCallback(() => {
    if (typeof window === 'undefined') return;
    try {
      localStorage.setItem(GLOBAL_CHECK_KEY, Date.now().toString());
    } catch (e) {
      // Игнорируем ошибки
    }
  }, []);

  // Load user and check subscription status
  const loadUserAndCheckSubscription = useCallback(async (skipLoadingState = false) => {
    // ПРОВЕРКА: Если на другой вкладке недавно была проверка, используем только кэш
    if (isGlobalCheckInProgress() && !skipLoadingState) {
      console.log('[StockPageAuthWrapper] Global check in progress, using cache only');
      const cached = getCachedAuth();
      if (cached && cached.user) {
        setUser(cached.user);
        const entitlementsCache = readEntitlementsCache();
        if (entitlementsCache && entitlementsCache.userId === cached.user.id) {
          safeSetIsPaid(entitlementsCache.isPaid, false);
          safeSetEntitlementsStatus(entitlementsCache.isPaid ? 'paid' : 'free');
          if (entitlementsCache.isPaid) wasPaidRef.current = true;
        } else if (wasPaidRef.current) {
          // Sticky premium: если был premium, показываем premium
          safeSetIsPaid(true, false);
          safeSetEntitlementsStatus('paid');
        } else {
          safeSetEntitlementsStatus('unknown');
        }
        setIsChecking(false);
        hasLoadedOnceRef.current = true;
        isInitialMountRef.current = false;
        isLoadingRef.current = false;
        return;
      }
    }

    // Защита от множественных одновременных вызовов
    // НО: если загрузка уже была выполнена (hasLoadedOnceRef), разрешаем повторную загрузку
    // Это важно для новых вкладок, которые открываются после того, как первая уже загрузилась
    if (isLoadingRef.current && !hasLoadedOnceRef.current) {
      console.log('=== LOAD USER ALREADY IN PROGRESS, SKIPPING ===');
      return;
    }
    
    // Если данные уже были загружены, но isLoadingRef все еще true (например, из-за ошибки),
    // сбрасываем флаг и разрешаем повторную загрузку
    if (isLoadingRef.current && hasLoadedOnceRef.current) {
      console.log('=== RESETTING LOADING FLAG FOR RETRY ===');
      isLoadingRef.current = false;
    }
    
    isLoadingRef.current = true;
    markGlobalCheckStart(); // Помечаем начало проверки
    
    // Гарантируем, что флаги будут сброшены даже при ошибке
    const resetFlags = () => {
      setIsChecking(false);
      hasLoadedOnceRef.current = true;
      isInitialMountRef.current = false;
      isLoadingRef.current = false;
    };
    
    try {
      // Показываем загрузку только при первом монтировании, если навигация уже готова,
      // и только если данные еще не загружены
      // Не показываем загрузку при возврате на вкладку или если данные уже загружены
      const shouldShowLoading = isInitialMountRef.current && !skipLoadingState && !hasLoadedOnceRef.current && isNavigationReady;
      
      // В новых окнах навигация может быть не готова, но мы все равно должны показать загрузку
      // если это первое монтирование и данные еще не загружены
      const isFirstLoad = isInitialMountRef.current && !hasLoadedOnceRef.current;
      
      if (shouldShowLoading || (isFirstLoad && !skipLoadingState)) {
        setIsChecking(true);
      }
      
      // Устанавливаем максимальный таймаут для гарантированного сброса флага загрузки
      const maxTimeoutId = setTimeout(() => {
        console.warn('Maximum timeout reached, resetting loading state');
        resetFlags();
      }, 5000); // Максимум 5 секунд на загрузку
      
      try {
        // Сначала проверяем кэш
        const cached = getCachedAuth();
        if (cached && cached.user) {
          // Используем кэшированного пользователя сразу
          setUser(cached.user);
          resetFlags();
          
          // Загружаем entitlements из кэша мгновенно
          const entitlementsCache = readEntitlementsCache();
          if (entitlementsCache && cached.user && entitlementsCache.userId === cached.user.id) {
            safeSetIsPaid(entitlementsCache.isPaid, false); // Кэш - не подтвержденный источник
            safeSetEntitlementsStatus(entitlementsCache.isPaid ? 'paid' : 'free');
            if (entitlementsCache.isPaid) wasPaidRef.current = true; // Запоминаем, что был premium
          } else {
            // Если кэша нет, устанавливаем unknown и загружаем в фоне
            // НО: если wasPaidRef.current = true, используем его для оптимистичного отображения
            if (wasPaidRef.current) {
              // Если пользователь был premium, оптимистично показываем paid
              safeSetIsPaid(true, false);
              safeSetEntitlementsStatus('paid');
            } else {
              safeSetEntitlementsStatus('unknown');
            }
          }
          
          // Обновляем entitlements в фоне (не блокируем UI)
          if (cached.user) {
            const requestId = ++entitlementsRequestIdRef.current;
            (async () => {
              try {
                const result = await getEntitlements(cached.user!.id);
                
                // Проверяем, что запрос еще актуален
                if (requestId !== entitlementsRequestIdRef.current) {
                  console.log('[StockPageAuthWrapper] Entitlements request outdated, ignoring');
                  return;
                }
                
                // Проверяем, изменился ли статус перед обновлением
                const newIsPaid = result.isPaid;
                const newStatus = newIsPaid ? 'paid' : 'free';
                if (isPaid !== newIsPaid || entitlementsStatus !== newStatus) {
                  safeSetIsPaid(newIsPaid, true); // Подтвержденный источник - getEntitlements
                  safeSetEntitlementsStatus(newStatus);
                }
              } catch (error) {
                console.error('Error checking subscription:', error);
                
                // Проверяем актуальность запроса перед обновлением state
                if (requestId !== entitlementsRequestIdRef.current) {
                  console.log('[StockPageAuthWrapper] Entitlements error handling outdated, ignoring');
                  return;
                }
                
                // При ошибке используем кэш, если есть
                const fallbackCache = readEntitlementsCache();
                if (fallbackCache && cached.user && fallbackCache.userId === cached.user.id) {
                  safeSetIsPaid(fallbackCache.isPaid, false); // Кэш - не подтвержденный источник
                  safeSetEntitlementsStatus(fallbackCache.isPaid ? 'paid' : 'free');
                  if (fallbackCache.isPaid) wasPaidRef.current = true;
                } else {
                  // Если кэша нет, оставляем unknown (не понижаем до free)
                  safeSetEntitlementsStatus('unknown');
                }
              }
            })();
          }
          
          // Обновляем авторизацию в фоне (без блокировки UI)
          executeAuthRequest().then(({ user: freshUser }) => {
            if (freshUser && freshUser.id === cached.user?.id) {
              setCachedAuth(freshUser);
              setUser(freshUser);
            }
            // Важно: НЕ чистим кэш на freshUser=null
            // Это может быть временный null на фоне refresh/гонок/отвалившейся сети
            // Кэш очищается только при явном SIGNED_OUT событии
          });
          
          return;
        }
        
        // Если кэша нет или он устарел, делаем запрос через очередь
        const { user: currentUser, error: userError } = await executeAuthRequest();
        
        // Обработка таймаута: Timeout == "не смогли проверить", а не "user = null"
        if (userError && userError.message === 'Timeout') {
          console.warn('Auth request timeout, keeping cached state if available');
          
          // Если есть старый кэш (даже устаревший), используем его при таймауте
          const oldCached = getCachedAuth();
          if (!oldCached) {
            // Пробуем прочитать кэш напрямую, даже если он устарел
            try {
              const cached = localStorage.getItem(AUTH_CACHE_KEY);
              if (cached && cached !== 'null') {
                const oldUser = JSON.parse(cached);
                setUser(oldUser);
                // Загружаем entitlements из кэша, если есть
                const entitlementsCache = readEntitlementsCache();
                if (entitlementsCache && entitlementsCache.userId === oldUser.id) {
                  safeSetIsPaid(entitlementsCache.isPaid, false);
                  safeSetEntitlementsStatus(entitlementsCache.isPaid ? 'paid' : 'free');
                  if (entitlementsCache.isPaid) wasPaidRef.current = true;
                } else {
                  safeSetEntitlementsStatus('unknown');
                }
                resetFlags();
                
                // Планируем retry через 1-2 секунды
                setTimeout(() => {
                  if (!isLoadingRef.current) {
                    executeAuthRequest().then(({ user: retryUser, error: retryError }) => {
                      if (retryUser && !retryError) {
                        setCachedAuth(retryUser);
                        setUser(retryUser);
                      }
                    });
                  }
                }, 1500);
                
                return;
              }
            } catch (e) {
              console.warn('Error reading old cache:', e);
            }
          } else if (oldCached.user) {
            // Используем кэшированного пользователя
            setUser(oldCached.user);
            // Загружаем entitlements из кэша, если есть
            const entitlementsCache = readEntitlementsCache();
            if (entitlementsCache && entitlementsCache.userId === oldCached.user.id) {
              safeSetIsPaid(entitlementsCache.isPaid, false);
              safeSetEntitlementsStatus(entitlementsCache.isPaid ? 'paid' : 'free');
              if (entitlementsCache.isPaid) wasPaidRef.current = true;
            } else {
              safeSetEntitlementsStatus('unknown');
            }
            resetFlags();
            
            // Планируем retry через 1-2 секунды
            setTimeout(() => {
              if (!isLoadingRef.current) {
                executeAuthRequest().then(({ user: retryUser, error: retryError }) => {
                  if (retryUser && !retryError) {
                    setCachedAuth(retryUser);
                    setUser(retryUser);
                  }
                });
              }
            }, 1500);
            
            return;
          }
          
          // Если кэша нет вообще, просто сбрасываем флаги
          resetFlags();
          
          // Планируем retry через 1-2 секунды
          setTimeout(() => {
            if (!isLoadingRef.current) {
              executeAuthRequest().then(({ user: retryUser, error: retryError }) => {
                if (retryUser && !retryError) {
                  setCachedAuth(retryUser);
                  setUser(retryUser);
                }
              });
            }
          }, 1500);
          
          return;
        }
        
        // Сохраняем в кэш только если нет ошибки или ошибка не таймаут
        if (!userError || userError.message !== 'Timeout') {
          setCachedAuth(currentUser);
        }
        
        if (userError) {
          // Логируем только реальные ошибки (не таймаут, не просто отсутствие пользователя, не отсутствие сессии)
          const errorMessage = userError.message || (typeof userError === 'string' ? userError : '');
          const isExpectedError = errorMessage === 'Timeout' || 
                                  errorMessage === 'Auth session missing!' || 
                                  errorMessage.includes('Auth session missing');
          
          if (errorMessage && !isExpectedError && Object.keys(userError).length > 0) {
            console.error('Error getting user:', errorMessage || userError);
          }
          // При реальной ошибке НЕ сбрасываем кэш - это может быть временная ошибка сети
          // Кэш очищается только при явном SIGNED_OUT событии
          // Используем кэш, если есть
          const fallbackCached = getCachedAuth();
          if (fallbackCached && fallbackCached.user) {
            setUser(fallbackCached.user);
            const entitlementsCache = readEntitlementsCache();
            if (entitlementsCache && entitlementsCache.userId === fallbackCached.user.id) {
              safeSetIsPaid(entitlementsCache.isPaid, false);
              safeSetEntitlementsStatus(entitlementsCache.isPaid ? 'paid' : 'free');
              if (entitlementsCache.isPaid) wasPaidRef.current = true;
            } else {
              safeSetEntitlementsStatus('unknown');
            }
          } else {
            // При ошибке и отсутствии кэша не сбрасываем в free
            // Оставляем unknown, чтобы не показывать free для премиум пользователей
            safeSetEntitlementsStatus('unknown');
          }
          resetFlags();
          return;
        }
        
        setUser(currentUser);
        
        // Если пользователь есть, сразу показываем контент и проверяем подписку в фоне
        if (currentUser) {
          resetFlags();
          
          // Загружаем entitlements из кэша мгновенно
          const entitlementsCache = readEntitlementsCache();
          if (entitlementsCache && entitlementsCache.userId === currentUser.id) {
            safeSetIsPaid(entitlementsCache.isPaid, false);
            safeSetEntitlementsStatus(entitlementsCache.isPaid ? 'paid' : 'free');
            if (entitlementsCache.isPaid) wasPaidRef.current = true;
          } else {
            // Если кэша нет, устанавливаем unknown и загружаем в фоне
            // НО: если wasPaidRef.current = true, используем его для оптимистичного отображения
            if (wasPaidRef.current) {
              // Если пользователь был premium, оптимистично показываем paid
              safeSetIsPaid(true, false);
              safeSetEntitlementsStatus('paid');
            } else {
              safeSetEntitlementsStatus('unknown');
            }
          }
          
          // Проверяем подписку в фоне (не блокируем отображение контента)
          const requestId = ++entitlementsRequestIdRef.current;
          (async () => {
            try {
              const result = await getEntitlements(currentUser.id);
              
              // Проверяем, что запрос еще актуален
              if (requestId !== entitlementsRequestIdRef.current) {
                console.log('[StockPageAuthWrapper] Entitlements request outdated, ignoring');
                return;
              }
              
              // Проверяем, изменился ли статус перед обновлением
              const newIsPaid = result.isPaid;
              const newStatus = newIsPaid ? 'paid' : 'free';
              if (isPaid !== newIsPaid || entitlementsStatus !== newStatus) {
                safeSetIsPaid(newIsPaid, true); // Подтвержденный источник
                safeSetEntitlementsStatus(newStatus);
              }
            } catch (error) {
              console.error('Error checking subscription:', error);
              
              // Проверяем актуальность запроса перед обновлением state
              if (requestId !== entitlementsRequestIdRef.current) {
                console.log('[StockPageAuthWrapper] Entitlements error handling outdated, ignoring');
                return;
              }
              
              // При ошибке используем кэш, если есть
              const fallbackCache = readEntitlementsCache();
              if (fallbackCache && fallbackCache.userId === currentUser.id) {
                safeSetIsPaid(fallbackCache.isPaid, false);
                safeSetEntitlementsStatus(fallbackCache.isPaid ? 'paid' : 'free');
                if (fallbackCache.isPaid) wasPaidRef.current = true;
              } else {
                // Если кэша нет, оставляем unknown (не понижаем до free)
                // НО: если wasPaidRef.current = true, используем его для оптимистичного отображения
                if (wasPaidRef.current) {
                  safeSetIsPaid(true, false);
                  safeSetEntitlementsStatus('paid');
                } else {
                  safeSetEntitlementsStatus('unknown');
                }
              }
            }
          })();
        } else {
          // Если пользователя нет, сбрасываем флаг проверки
          wasPaidRef.current = false; // Сбрасываем sticky premium при logout
          setIsPaid(false);
          safeSetEntitlementsStatus('free');
          lastEntitlementsStatusRef.current = null;
          resetFlags();
        }
      } catch (innerError) {
        throw innerError;
      } finally {
        // Гарантированно очищаем таймаут в одном месте, независимо от пути выполнения
        clearTimeout(maxTimeoutId);
      }
    } catch (error) {
      console.error('Error loading user or subscription:', error);
      setUser(null);
      setIsPaid(false);
      safeSetEntitlementsStatus('free');
      lastEntitlementsStatusRef.current = null;
      resetFlags();
    }
  }, [isNavigationReady, isGlobalCheckInProgress, markGlobalCheckStart]);

  // Загружаем данные, когда навигация готова или после таймаута
  useEffect(() => {
    // Загружаем только при первом монтировании
    // НО: если hasLoadedOnceRef.current = true, это означает, что данные уже были загружены,
    // но возможно произошла ошибка, поэтому разрешаем повторную загрузку
    if (!isInitialMountRef.current) return;
    
    // Если загрузка уже идет и данные еще не были загружены, не запускаем еще одну
    if (isLoadingRef.current && !hasLoadedOnceRef.current) return;
    
    // В новых окнах навигация может не стать готовой быстро,
    // поэтому устанавливаем более короткий таймаут и загружаем независимо
    let timeoutId: NodeJS.Timeout;
    let fallbackTimeoutId: NodeJS.Timeout;
    
    // Случайная задержка для новых окон (0-300мс) для предотвращения одновременных запросов
    const randomDelay = Math.floor(Math.random() * 300);
    
    const loadData = () => {
      // Разрешаем загрузку, если это первое монтирование или данные уже были загружены (для retry)
      if (isInitialMountRef.current && (!hasLoadedOnceRef.current || isLoadingRef.current === false)) {
        loadUserAndCheckSubscription();
      }
    };
    
    if (isNavigationReady) {
      // Если навигация уже готова, загружаем с небольшой задержкой
      timeoutId = setTimeout(loadData, randomDelay);
    } else {
      // Если навигация не готова, устанавливаем короткий таймаут (1 секунда + задержка)
      // Это особенно важно для новых окон, где навигация может загружаться медленнее
      timeoutId = setTimeout(() => {
        console.warn('Navigation ready timeout, loading data anyway');
        loadData();
      }, 1000 + randomDelay); // Уменьшили до 1 секунды для новых окон
    }
    
    // Дополнительный таймаут на случай, если навигация вообще не станет готовой
    fallbackTimeoutId = setTimeout(() => {
      if (isInitialMountRef.current && (!hasLoadedOnceRef.current || isLoadingRef.current === false)) {
        console.warn('Fallback timeout: loading data regardless of navigation state');
        loadData();
      }
    }, 2000 + randomDelay); // Максимум 2 секунды ожидания
    
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      if (fallbackTimeoutId) clearTimeout(fallbackTimeoutId);
    };
  }, [isNavigationReady, loadUserAndCheckSubscription]);
  
  // Дополнительный эффект: если навигация стала готовой после таймаута, все равно загружаем
  useEffect(() => {
    if (isNavigationReady && isInitialMountRef.current && !hasLoadedOnceRef.current && !isLoadingRef.current) {
      loadUserAndCheckSubscription();
    }
  }, [isNavigationReady, loadUserAndCheckSubscription]);

  // Синхронизируем user из AuthProvider - НЕ перезагружаем entitlements при каждом изменении
  useEffect(() => {
    // Только обновляем user, НЕ перезагружаем entitlements если они уже загружены
    if (authUser) {
      // Проверяем, изменился ли user
      if (user?.id !== authUser.id) {
        setUser(authUser);
        // Только если user действительно изменился И данные еще не загружены, перезагружаем
        if (!hasLoadedOnceRef.current) {
          // Используем кэш если есть, не делаем новый запрос
          const cached = getCachedAuth();
          if (cached && cached.user && cached.user.id === authUser.id) {
            // Пользователь уже в кэше, просто обновляем state
            const entitlementsCache = readEntitlementsCache();
            if (entitlementsCache && entitlementsCache.userId === authUser.id) {
              safeSetIsPaid(entitlementsCache.isPaid, false);
              safeSetEntitlementsStatus(entitlementsCache.isPaid ? 'paid' : 'free');
              if (entitlementsCache.isPaid) wasPaidRef.current = true;
            } else if (wasPaidRef.current) {
              safeSetIsPaid(true, false);
              safeSetEntitlementsStatus('paid');
            }
            hasLoadedOnceRef.current = true;
            isLoadingRef.current = false;
            return;
          }
          // Только если кэша нет, загружаем
          loadUserAndCheckSubscription(true);
        }
      }
    } else if (!authUser && user) {
      // Только при реальном logout
      wasPaidRef.current = false;
      setIsPaid(false);
      safeSetEntitlementsStatus('free');
      setUser(null);
      lastEntitlementsStatusRef.current = null;
    }
  }, [authUser, authLoading]); // Убрали loadUserAndCheckSubscription из зависимостей
  
  // Инициализируем lastEntitlementsStatusRef из кэша при монтировании
  // wasPaidRef уже инициализирован синхронно при объявлении
  useEffect(() => {
    const cached = readInitialCache();
    if (cached) {
      // wasPaidRef уже инициализирован синхронно, только обновляем lastEntitlementsStatusRef
      lastEntitlementsStatusRef.current = {
        isPaid: cached.isPaid,
        status: cached.status
      };
    }
  }, []); // Только при монтировании

  // Гарантированный сброс флага загрузки при размонтировании компонента
  useEffect(() => {
    return () => {
      setIsChecking(false);
      isLoadingRef.current = false;
      // Очищаем таймаут дебаунсинга
      if (entitlementsUpdateTimeoutRef.current) {
        clearTimeout(entitlementsUpdateTimeoutRef.current);
        entitlementsUpdateTimeoutRef.current = null;
      }
    };
  }, []);

  // Обновляем entitlements при возвращении на страницу после простоя
  useEffect(() => {
    if (typeof window === 'undefined') return;
    
    let lastVisibilityTime = Date.now();
    const IDLE_THRESHOLD = 5 * 60 * 1000; // 5 минут - после этого обновляем entitlements
    
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        const idleTime = Date.now() - lastVisibilityTime;
        
        // Если страница была скрыта больше 5 минут и есть пользователь
        if (idleTime > IDLE_THRESHOLD && user) {
          console.log('[StockPageAuthWrapper] Page became visible after idle, refreshing entitlements');
          
          // Проактивно обновляем entitlements в фоне
          const requestId = ++entitlementsRequestIdRef.current;
          (async () => {
            try {
              const result = await getEntitlements(user.id, true); // forceRefresh = true
              
              // Проверяем, что запрос еще актуален
              if (requestId !== entitlementsRequestIdRef.current) {
                return;
              }
              
              const newIsPaid = result.isPaid;
              const newStatus = newIsPaid ? 'paid' : 'free';
              
              // Обновляем статус только если он изменился
              if (isPaid !== newIsPaid || entitlementsStatus !== newStatus) {
                safeSetIsPaid(newIsPaid, true); // Подтвержденный источник
                safeSetEntitlementsStatus(newStatus);
              }
            } catch (error) {
              console.warn('[StockPageAuthWrapper] Error refreshing entitlements on visibility change:', error);
              // При ошибке сохраняем текущий статус (sticky premium)
            }
          })();
        }
      } else {
        // Страница скрылась - запоминаем время
        lastVisibilityTime = Date.now();
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [user, isPaid, entitlementsStatus, safeSetIsPaid, safeSetEntitlementsStatus]);

  // Check subscription status after auth
  const checkSubscriptionStatus = useCallback(async (): Promise<boolean> => {
    if (!user) return false;
    const requestId = ++entitlementsRequestIdRef.current;
    try {
      const result = await getEntitlements(user.id);
      
      // Проверяем, что запрос еще актуален
      if (requestId !== entitlementsRequestIdRef.current) {
        console.log('[StockPageAuthWrapper] Subscription check outdated, ignoring');
        return false;
      }
      
      // Проверяем, изменился ли статус перед обновлением
      const newIsPaid = result.isPaid;
      const newStatus = newIsPaid ? 'paid' : 'free';
      if (isPaid !== newIsPaid || entitlementsStatus !== newStatus) {
        safeSetIsPaid(newIsPaid, true); // Подтвержденный источник
        safeSetEntitlementsStatus(newStatus);
      }
      return !result.isPaid; // Return true if should show subscription modal
    } catch (error) {
      console.error('Error checking subscription status:', error);
      
      // Проверяем актуальность запроса перед обновлением state
      if (requestId !== entitlementsRequestIdRef.current) {
        return false;
      }
      
      return false;
    }
  }, [user]);

  // Anonymous users can browse any stock page freely.
  // Auth modal is triggered only when they interact with gated content.

  const handleAuthSuccess = useCallback(async () => {
    await loadUserAndCheckSubscription();
    setAuthModalOpen(false);
    // If the page was rendered for an anonymous user, the server omitted
    // gated sections (All Metrics, Charts, Financials) from the HTML.
    // router.refresh() re-runs the server component so those sections
    // appear without a full reload.
    if (initialAccessLevel === 'anonymous') {
      setIsRefreshing(true);
      router.refresh();
    }
  }, [loadUserAndCheckSubscription, initialAccessLevel, router]);

  // When router.refresh() completes, initialAccessLevel prop updates from the server.
  // Turn off the spinner as soon as the page is no longer 'anonymous'.
  useEffect(() => {
    if (isRefreshing && initialAccessLevel !== 'anonymous') {
      setIsRefreshing(false);
    }
  }, [initialAccessLevel, isRefreshing]);

  const handleSubscriptionClose = useCallback(() => {
    setSubscriptionModalOpen(false);
    setSubscriptionModalMessage(null);
    setSubscriptionModalTitle(null);
    setSubscriptionHighlightFeature(null);
  }, []);

  const openUpgradeModal = useCallback((opts?: { title?: string; message?: string; highlightFeature?: string }) => {
    setSubscriptionModalTitle(opts?.title ?? null);
    setSubscriptionModalMessage(opts?.message ?? null);
    setSubscriptionHighlightFeature(opts?.highlightFeature ?? null);
    setSubscriptionModalOpen(true);
  }, []);

  const stockViewTrackedRef = useRef<string | null>(null);
  useEffect(() => {
    if (stockViewTrackedRef.current === fullTicker) return;
    stockViewTrackedRef.current = fullTicker;
    trackStockViewed({
      full_ticker: fullTicker,
      ticker: stockMeta?.ticker,
      name: stockMeta?.name,
      sector: stockMeta?.sector,
      country: stockMeta?.country,
      access_level: initialAccessLevel ?? 'unknown',
      is_authenticated: initialAccessLevel != null && initialAccessLevel !== 'anonymous',
    });
  }, [fullTicker, initialAccessLevel, stockMeta?.ticker, stockMeta?.name, stockMeta?.sector, stockMeta?.country]);

  // Derived access level for the rest of the tree. We prefer to compute it
  // here (instead of trusting initialAccessLevel forever) so that login and
  // upgrade events are reflected automatically.
  // Anonymous → 'anonymous'; paid → 'paid'; logged-in free → 'within-limit'.
  //
  // IMPORTANT: isPaid is checked FIRST so paid users (known via server-side
  // `initialAccessLevel` or the entitlements cache) never see the paywall
  // even during the brief moment after hydration when `user` is still null.
  let accessLevel: StockAccessLevel;
  if (isPaid) {
    accessLevel = 'paid';
  } else if (!user) {
    // No paid status and no user yet. If the server told us the user is
    // logged-in free, trust that until the client confirms — prevents a
    // brief 'anonymous' flash.
    accessLevel = (initialAccessLevel && initialAccessLevel !== 'anonymous')
      ? 'within-limit'
      : 'anonymous';
  } else {
    // Depth-gate: any logged-in non-paid user is free showcase access.
    accessLevel = 'within-limit';
  }

  // Legacy flag for the Blurred* helpers (old stock layouts). The current
  // page no longer blurs for anonymous visitors.
  const shouldBlur = !isPaid && !user;

  // Настройка BroadcastChannel для синхронизации entitlements между вкладками
  useEffect(() => {
    const cleanup = setupEntitlementsBroadcastListener((cache) => {
      if (user && cache.userId === user.id) {
        // Проверяем timestamp кэша - игнорируем устаревшие обновления
        const currentCache = readEntitlementsCache();
        if (currentCache && currentCache.timestamp > cache.timestamp) {
          console.log('[StockPageAuthWrapper] Ignoring outdated broadcast update', {
            currentTimestamp: currentCache.timestamp,
            broadcastTimestamp: cache.timestamp
          });
          return;
        }
        
        // Проверяем, изменился ли статус перед обновлением
        const lastStatus = lastEntitlementsStatusRef.current;
        if (lastStatus && 
            lastStatus.isPaid === cache.isPaid && 
            lastStatus.status === (cache.isPaid ? 'paid' : 'free')) {
          return; // Статус не изменился
        }
        
        // Инвалидируем текущий запрос, чтобы новые обновления не перезаписали broadcast
        entitlementsRequestIdRef.current++;
        
        // Sticky premium: broadcast с downgrade НЕ считается подтвержденным источником
        // Только upgrade (paid) или если текущий статус уже free - считаем подтвержденным
        const isUpgrade = !wasPaidRef.current && cache.isPaid;
        const isConfirmedDowngrade = wasPaidRef.current && !cache.isPaid && lastStatus && lastStatus.isPaid === false;
        const fromConfirmedSource: boolean = !!(isUpgrade || isConfirmedDowngrade);
        
        // Для downgrade от paid к free через broadcast применяем sticky premium
        if (wasPaidRef.current && !cache.isPaid && !fromConfirmedSource) {
          console.log('[StockPageAuthWrapper] Sticky premium: ignoring broadcast downgrade to free');
          return; // Не обновляем статус, оставляем premium
        }
        
        safeSetIsPaid(cache.isPaid, fromConfirmedSource);
        safeSetEntitlementsStatus(cache.isPaid ? 'paid' : 'free');
      }
    });
    
    return cleanup;
  }, [user]);

  const contextValue: AuthContextType = {
    user,
    isPaid,
    shouldBlur,
    accessLevel,
    entitlementsStatus,
    wasPremiumEver: wasPaidRef.current,
    authModalDismissed,
    subscriptionDismissed,
    setSubscriptionDismissed,
    top30Stocks,
    setTop30Stocks,
    setAuthModalOpen,
    setAuthMode,
    setSubscriptionModalOpen,
    openUpgradeModal,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      <ShareNavProvider>
      {isChecking ? (
        <div className="min-h-screen site-bg flex items-center justify-center">
          <div style={{ padding: '48px 16px' }}>
            <BrailleSpinner />
          </div>
        </div>
      ) : (
        children
      )}

      {isRefreshing && (
        <div style={{
          position: 'fixed',
          inset: 0,
          top: '48px',
          background: 'rgba(26,26,26,0.75)',
          backdropFilter: 'blur(4px)',
          WebkitBackdropFilter: 'blur(4px)',
          zIndex: 50,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <BrailleSpinner label="Loading your reports…" />
        </div>
      )}
      </ShareNavProvider>
      
      <AnonScrollSignupPrompt fullTicker={fullTicker} authModalOpen={authModalOpen} />

      <AuthModal
        isOpen={authModalOpen}
        onClose={() => {
          setAuthModalOpen(false);
        }}
        mode={authMode}
        onModeChange={setAuthMode}
        onAuthSuccess={handleAuthSuccess}
      />
      
      <SubscriptionModal
        isOpen={subscriptionModalOpen}
        onClose={handleSubscriptionClose}
        title={subscriptionModalTitle || 'Choose a subscription plan'}
        message={subscriptionModalMessage}
        highlightFeature={subscriptionHighlightFeature}
        analyticsContext={{ surface: 'stock' }}
      />
    </AuthContext.Provider>
  );
}

